import React, { ReactElement } from 'react';
import Box from '@mui/material/Box';
import ListItemWrapper from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Chip } from '../Chip';
import { OptionsDropdown, OptionsParams } from '../OptionsDropdown';
import { Typography, CustomTypographyVariant } from '../Typography';
export interface LinkedListItemProps {
  /**
   * title / header copy
   */
  title: string;
  /**
   * the variant to use to render the title with
   */
  titleVariant?: CustomTypographyVariant;
  /**
   * the subtitle
   */
  subTitle?: string | ReactElement;
  /**
   * the icon to show in the card
   */
  icon?: ReactElement;
  /**
   * whether we are in mobile breakpoint
   */
  isSmallDevice: boolean;
  /**
   * optional, note: this only renders if isSupplier is falsy, it defines the list of options to
   * show when clicking on the ellipsis button
   */
  optionsParams?: OptionsParams;
  /**
   * optional, the status text to show next to the title
   */
  status?: string;
  /**
   * optional, the status chip text to show next to the title
   */
  tagText?: string;
  /**
   * optional, whether to enable/disable the ellipsis button
   */
  actionsEnabled?: boolean;
  /**
   * child component(s) which will be rendered as the last item in the ListItemWrapper
   */
  children?: JSX.Element | JSX.Element[];
}
/**
 * Renders a generic list component
 */
export const LinkedListItem: React.FC<React.PropsWithChildren<LinkedListItemProps>> = ({
  title,
  titleVariant,
  subTitle,
  icon,
  isSmallDevice,
  optionsParams,
  status,
  tagText,
  actionsEnabled = true,
  children,
}: LinkedListItemProps) => {
  const isOptionsDropdownVisible = actionsEnabled && optionsParams?.options && optionsParams?.options?.length >= 1;
  return (
    <ListItemWrapper
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: '16px',
        alignItems: 'center',
        padding: 0,
        position: 'relative',
      }}
      data-testid="list-item"
    >
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText>
        {subTitle ? subTitle : null}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 4,
            alignItems: 'center',
          }}
        >
          <Typography variant={titleVariant}>{title}</Typography>
          {tagText ? <Chip variant="outlined" label={tagText} data-testid="list-item-tag" /> : ''}
          {Boolean(status) && (
            <Typography
              variant="body2"
              sx={{
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: '0.25rem',
                padding: 1,
              }}
            >
              {status}
            </Typography>
          )}
        </Box>
      </ListItemText>
      {isOptionsDropdownVisible ? (
        <OptionsDropdown isSmallDevice={isSmallDevice} optionsParams={optionsParams} />
      ) : null}
      {children}
    </ListItemWrapper>
  );
};
